import * as React from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { Combobox, type Option as ComboboxOption } from './combobox';

export type Option<T extends string | null> = ComboboxOption<T>;

interface ComboboxPopoverProps<T extends string> {
  children: React.ReactNode;
  endAdornment: React.ReactNode;
  placeholder: string;
  emptyMessage: React.ReactNode | string;
  currentOption?: Option<T>;
  options: Option<T>[];
  options2?: Option<T>[];
  options3?: Option<T>[];
  onChange: (value: T) => void;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  footerOptions?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverContentClassName?: string;
  stayOpenAfterSelection?: boolean;
  alignOffset?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  alwaysShowValues?: string[];
  search?: string;
  setSearch?: (search: string) => void;
}

export function ComboboxPopover<T extends string>({
  children,
  endAdornment,
  placeholder,
  emptyMessage,
  currentOption,
  options,
  options2,
  options3,
  onChange,
  side,
  align,
  footerOptions,
  open: openProp = false,
  onOpenChange,
  popoverContentClassName,
  stayOpenAfterSelection = false,
  alignOffset,
  onKeyDown,
  alwaysShowValues = [],
  search,
  setSearch,
}: ComboboxPopoverProps<T>) {
  const [open, setOpen] = React.useState(openProp);

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  return (
    <Popover
      open={open}
      onOpenChange={(open: boolean) => {
        setOpen(open);
        onOpenChange?.(open);
      }}
      modal
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={cn('w-fit p-0', popoverContentClassName)}
        side={side}
        align={align}
        alignOffset={alignOffset}
      >
        <Combobox
          currentOption={currentOption}
          options={options}
          options2={options2}
          options3={options3}
          onChange={onChange}
          footerOptions={footerOptions}
          endAdornment={endAdornment}
          placeholder={placeholder}
          emptyMessage={emptyMessage}
          setOpen={setOpen}
          stayOpenAfterSelection={stayOpenAfterSelection}
          onKeyDown={onKeyDown}
          alwaysShowValues={alwaysShowValues}
          search={search}
          setSearch={setSearch}
        ></Combobox>
      </PopoverContent>
    </Popover>
  );
}
